import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { API, apiClient } from '../api_clients';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import { extractErrorMessage } from './axiosIntance';
import { mapApiUserToEntity } from '../mappers/api-to-entity';
import { toast } from 'sonner';
import { ResponsesUploadUrlResponse, ResponsesUploadUrlResponseData } from '../api_clients/api';
import axios from 'axios';

// Re-export user types from API
export type User = API.ResponsesUser;
export type UpdateUserProfileRequest = API.UpdateUserProfileRequest
export type Organization = API.ResponsesOrganization;

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get current user profile
 */
async function getMe(): Promise<User> {
    const response = await apiClient.usersApi.getUserProfile("");
    return response.data.data?.user;
}
/**
 * Update user profile
 */
async function updateUserProfile(data: API.UpdateUserProfileRequest): Promise<User> {
    const response = await apiClient.usersApi.updateUserProfile("", data);
    return response.data.data?.user!;
}

/**
 * Upload user profile picture
 */
async function uploadProfilePicture(file: File): Promise<User> {
    const response = await apiClient.usersApi.getProfilePictureUploadUrl('jpeg', '');

    if (!response.data.data) {
        throw new Error('Failed to get upload URL');
    }

    const { uploadUrl, accessUlr } = response.data.data;

    try {
        // Upload file to S3 using the signed URL
        await axios.put(uploadUrl, file, {
            headers: {
                'Content-Type': file.type
            },
        });
    } catch (error) {
        console.error('[S3 Upload] Upload failed:', error);
        throw error;
    }

    // Update user profile
    const updatedUser = await updateUserProfile({ profilePhotoUrl: accessUlr })
    return updatedUser
}

/**
 * Delete user account
 */
// async function deleteAccount(): Promise<void> {
//     await apiClient.usersApi.dele("");
// }

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to get current user profile
 * Automatically updates auth store on success and handles unauthorized errors
 */
export function useGetMe(enabled: boolean = true) {
    const { clearAuth } = useAuthStore();
    const { setUser, clearUser } = useUserStore();
    const query = useQuery({
        queryKey: ['auth', 'me'],
        queryFn: getMe,
        enabled,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Handle success - Update user store
    useEffect(() => {
        if (query.data) {
            const user = mapApiUserToEntity(query.data);
            setUser(user);
        }
    }, [query.data, setUser]);

    // Handle error - Logout if unauthorized
    useEffect(() => {
        if (query.error) {
            console.error('Failed to get user profile:', query.error);
            const errorMessage = extractErrorMessage(query.error);

            // If unauthorized, clear auth and user data
            if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
                clearAuth();
                clearUser();
            }
        }
    }, [query.error, clearAuth, clearUser]);
    return query;
}

/**
 * Hook to update user profile
 */
export function useUpdateUserProfile(hideToast: boolean = false) {
    const { setUser } = useUserStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateUserProfile,
        onSuccess: (data) => {
            // 1. Map and update store
            const user = mapApiUserToEntity(data);
            setUser(user);

            // 2. Update the query cache directly instead of invalidating
            // This prevents the race condition where refetch could return stale data
            queryClient.setQueryData(['auth', 'me'], data);

            // 3. Show success message
            if (!hideToast) {
                toast.success('Profile updated successfully!');
            }
        },
        onError: (error) => {
            const errorMessage = extractErrorMessage(error);
            if (!hideToast) {
                toast.error('Failed to update profile', {
                    description: errorMessage,
                });
            }
        },
    });
}

/**
 * Hook to upload profile picture
 */
export function useUploadProfilePicture() {
    const { setUser } = useUserStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: uploadProfilePicture,
        onSuccess: (updatedUser) => {
            // Update user profile
            const user = mapApiUserToEntity(updatedUser);
            setUser(user);

            // Invalidate user queries to fetch the updated profile picture
            queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });

            // Show success message
            toast.success('Profile picture uploaded successfully!');
        },
        onError: (error) => {
            const errorMessage = extractErrorMessage(error);
            toast.error('Failed to upload profile picture', {
                description: errorMessage,
            });
        },
    });
}

/**
 * Hook to delete user account
 */
// export function useDeleteAccount() {
//     const { logout: logoutStore } = useAuthStore();
//     const queryClient = useQueryClient();

//     return useMutation({
//         mutationFn: deleteAccount,
//         onSuccess: () => {
//             // 1. Logout user
//             logoutStore();

//             // 2. Clear all queries
//             queryClient.clear();

//             // 3. Show success message
//             toast.success('Account deleted successfully!');
//         },
//         onError: (error) => {
//             const errorMessage = extractErrorMessage(error);
//             toast.error('Failed to delete account', {
//                 description: errorMessage,
//             });
//         },
//     });
// }
